/**
 * Navigation contributed by the Naumene firm app.
 *
 * ── Two groups, in the order of the work ──────────────────────────────────
 * The sidebar used to be one flat list, which put "Forms" (something you set
 * up once a quarter) visually level with "Pipeline" (something you live in).
 * It is now two labelled groups:
 *
 *   Work     what you open every day — read the funnel, work the pipeline,
 *            ask the odd question the funnel cannot answer.
 *   Set up   what you configure and then leave alone — the forms clients
 *            fill in, the nudges that go out, the scoring rules, the stages.
 *
 * The group label is carried by the first item of each group (`section`),
 * which is how the shell draws headings; see DashboardLayoutRoute.
 *
 * ── Stages and Scoring are in the sidebar now ─────────────────────────────
 * They were reachable only from two icon buttons in the pipeline header, on
 * the theory that you configure columns while looking at the board. True,
 * but it made them the only two modules in the product with no address in
 * the navigation, and a firm admin setting the product up for the first time
 * had to know the icons were there. They sit under Set up; the pipeline
 * header keeps its shortcuts.
 *
 * `/f/:slug` and `/s/:token` are deliberately absent: those are the client's
 * routes, reached from an emailed link or a website embed, and they have no
 * place in staff navigation.
 */
import {
  BarChart3,
  Bell,
  Blocks,
  Briefcase,
  CalendarClock,
  CalendarDays,
  ClipboardList,
  FileSignature,
  KanbanSquare,
  LayoutTemplate,
  Mail,
  MailCheck,
} from 'lucide-react';
import type { NavItem } from './nav.base';

export const items: NavItem[] = [
  /* ── Work ─────────────────────────────────────────────────────────────── */
  /*
   * Overview first: it is what you read BEFORE you start working, because it
   * tells you where the funnel is leaking. Pipeline is where you then go and
   * do something about it. Ask (order 30, owned by nav.base.ts) closes the
   * group for the question neither of those answers.
   *
   * The line between the groups is daily-versus-occasional: everything under
   * Tools is configured once and left alone, so a surface used mid-task
   * belongs here even though it is powered by the same machinery.
   */
  { to: '/overview', label: 'Overview', icon: BarChart3, section: 'Work', order: 10 },
  { to: '/pipeline', label: 'Prospects', icon: KanbanSquare, order: 20 },
  /* Every matter opened from intake, and whether it reached the case
   * system. Beside the pipeline because it is where that pipeline ends. */
  { to: '/matter', label: 'Matters', icon: Briefcase, order: 22, minRole: 'paralegal' },
  /* The diary, not the settings screen it used to be. Somebody opens this to
   * see the day, which makes it work rather than setup — the meeting types
   * that used to be the whole page are now a section at the bottom of it. */
  /* Everything with a date, for a person or the firm: appointments, court
   * dates, deadlines, tasks, time off. */
  { to: '/calendar', label: 'Calendar', icon: CalendarDays, order: 24 },
  { to: '/appointment', label: 'Appointments', icon: CalendarClock, order: 25 },

  /* ── Templates ────────────────────────────────────────────────────────── */
  /*
   * The three things a firm writes once and reuses: the questions they ask,
   * the agreements they send, and the messages that chase. They were spread
   * across "Set up" and a tab inside Follow-up, which meant somebody looking
   * for saved wording had to know which machine happened to send it.
   */
  { to: '/form', label: 'Forms', icon: ClipboardList, section: 'Templates', order: 40 },
  { to: '/template', label: 'Documents', icon: FileSignature, order: 45, minRole: 'owner' },
  /* What each kind of matter records, its phases and deadline rules. Here
   * rather than under Work: it is set up once, like the forms beside it. */
  { to: '/matter/templates', label: 'Matter templates', icon: LayoutTemplate, order: 47, minRole: 'owner' },
  { to: '/email-template', label: 'Emails', icon: Mail, order: 50, minRole: 'owner' },

  /* ── Tools ────────────────────────────────────────────────────────────── */
  /*
   * The machinery that acts on all of the above — set up once, then left
   * alone. Settings (nav.admin) sorts into this group by order rather than
   * declaring it, since it belongs to another layer.
   *
   * Scoring and Stages are deliberately absent: both are pipeline
   * configuration and both already sit as labelled buttons in the pipeline
   * page's own header.
   */
  { to: '/follow-up', label: 'Automation', icon: MailCheck, section: 'Tools', order: 60, minRole: 'owner' },
  { to: '/notification', label: 'Notifications', icon: Bell, order: 68, minRole: 'owner' },
  /* Last in Tools, and below Notifications deliberately: it is the one screen
   * here that configures nothing. It answers "what does this talk to", which
   * is a question asked once while choosing the product and rarely after. */
  { to: '/integration', label: 'Integrations', icon: Blocks, order: 72, minRole: 'owner' },
];
